import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Calculator,
  DownloadSimple,
  FileText,
  FileCsv,
  TrendUp,
  TrendDown,
  CurrencyEur,
  CalendarBlank,
  ArrowsClockwise,
  SpinnerGap,
  ArrowUpRight,
  ArrowDownRight,
  Scales,
  FilePdf
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function Compta() {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [summary, setSummary] = useState(null);
  
  // Date range - default to current month
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  const [startDate, setStartDate] = useState(firstDay.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(lastDay.toISOString().split('T')[0]);

  useEffect(() => {
    loadSummary();
  }, []);

  async function loadSummary() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/accounting/summary`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setSummary(res.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    }
    setLoading(false);
  }

  async function downloadFEC() {
    setGenerating('fec');
    try {
      const response = await axios.get(`${API}/accounting/fec`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FEC_${startDate}_${endDate}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('FEC téléchargé');
    } catch (error) {
      toast.error('Erreur de génération du FEC');
    }
    setGenerating(null);
  }

  async function downloadCSV(type = 'all') {
    setGenerating('csv');
    try {
      const response = await axios.get(`${API}/accounting/export-csv`, {
        params: { start_date: startDate, end_date: endDate, export_type: type },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export_compta_${startDate}_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export CSV téléchargé');
    } catch (error) {
      toast.error('Erreur de génération du CSV');
    }
    setGenerating(null);
  }

  async function downloadPDF() {
    setGenerating('pdf');
    try {
      const response = await axios.get(`${API}/accounting/pdf-summary`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recap_compta_${startDate}_${endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF récapitulatif téléchargé');
    } catch (error) {
      toast.error('Erreur de génération du PDF');
    }
    setGenerating(null);
  }

  // Quick period selections
  function setCurrentMonth() {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(first.toISOString().split('T')[0]);
    setEndDate(last.toISOString().split('T')[0]);
  }

  function setPreviousMonth() {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    setStartDate(first.toISOString().split('T')[0]);
    setEndDate(last.toISOString().split('T')[0]);
  }

  function setCurrentQuarter() {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const first = new Date(now.getFullYear(), quarter * 3, 1);
    const last = new Date(now.getFullYear(), quarter * 3 + 3, 0);
    setStartDate(first.toISOString().split('T')[0]);
    setEndDate(last.toISOString().split('T')[0]);
  }

  function setCurrentYear() {
    const now = new Date();
    setStartDate(`${now.getFullYear()}-01-01`);
    setEndDate(`${now.getFullYear()}-12-31`);
  }

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="py-6 space-y-6" 
      data-testid="compta-page"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
          <Calculator size={20} className="text-violet-600" weight="duotone" />
        </div>
        <span className="text-lg font-semibold text-foreground">Comptabilité</span>
      </div>

      {/* Period Selection */}
      <div className="premium-card p-5">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Date début</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 rounded-xl"
                data-testid="start-date"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Date fin</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 rounded-xl"
                data-testid="end-date"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button onClick={setCurrentMonth} className="px-3 py-2 rounded-xl bg-muted hover:bg-muted/80 text-sm font-medium text-foreground transition-colors">
              Mois en cours
            </button>
            <button onClick={setPreviousMonth} className="px-3 py-2 rounded-xl bg-muted hover:bg-muted/80 text-sm font-medium text-foreground transition-colors">
              Mois précédent
            </button>
            <button onClick={setCurrentQuarter} className="px-3 py-2 rounded-xl bg-muted hover:bg-muted/80 text-sm font-medium text-foreground transition-colors">
              Trimestre
            </button>
            <button onClick={setCurrentYear} className="px-3 py-2 rounded-xl bg-muted hover:bg-muted/80 text-sm font-medium text-foreground transition-colors">
              Année
            </button>
          </div>
          
          <button onClick={loadSummary} disabled={loading} className="px-4 py-2 rounded-xl bg-black dark:bg-white text-white dark:text-black font-medium flex items-center gap-2 hover:opacity-90 transition-colors disabled:opacity-50" data-testid="refresh-btn">
            {loading ? <SpinnerGap size={18} className="animate-spin" /> : <ArrowsClockwise size={18} />}
            Actualiser
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Sales Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="premium-card p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <TrendUp size={18} className="text-emerald-600" weight="duotone" />
                </div>
                <span className="font-semibold text-foreground">Ventes</span>
              </div>
              <span className="text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-1 rounded-full font-medium">
                {summary.sales.count} factures
              </span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total HT</span>
                <span className="font-semibold text-foreground">{formatMoney(summary.sales.total_ht)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">TVA collectée</span>
                <span className="text-foreground">{formatMoney(summary.sales.total_tva)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="text-sm font-medium text-foreground">Total TTC</span>
                <span className="font-bold text-emerald-600">{formatMoney(summary.sales.total_ttc)}</span>
              </div>
            </div>
          </motion.div>

          {/* Purchases Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="premium-card p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <TrendDown size={18} className="text-amber-600" weight="duotone" />
                </div>
                <span className="font-semibold text-foreground">Achats</span>
              </div>
              <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-1 rounded-full font-medium">
                {summary.purchases.count} factures
              </span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total HT</span>
                <span className="font-semibold text-foreground">{formatMoney(summary.purchases.total_ht)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">TVA déductible</span>
                <span className="text-foreground">{formatMoney(summary.purchases.total_tva)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="text-sm font-medium text-foreground">Total TTC</span>
                <span className="font-bold text-amber-600">{formatMoney(summary.purchases.total_ttc)}</span>
              </div>
            </div>
          </motion.div>

          {/* Balance Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="premium-card p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Scales size={18} className="text-blue-600" weight="duotone" />
                </div>
                <span className="font-semibold text-foreground">Solde</span>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">TVA à reverser</span>
                <span className={`font-semibold ${summary.balance.tva_a_payer >= 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                  {formatMoney(summary.balance.tva_a_payer)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="text-sm font-medium text-foreground">Résultat HT</span>
                <span className={`font-bold text-lg ${summary.balance.resultat_ht >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatMoney(summary.balance.resultat_ht)}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Export Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="premium-card p-6"
      >
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <DownloadSimple size={20} className="text-violet-600" weight="duotone" />
          Exports Comptables
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* FEC Export */}
          <div className="border border-border rounded-2xl p-4 hover:border-violet-300 dark:hover:border-violet-700 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <FileText size={20} className="text-violet-600" weight="duotone" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">FEC</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Fichier des Écritures Comptables (norme DGFiP)
                </p>
                <button 
                  onClick={downloadFEC}
                  disabled={generating === 'fec'}
                  className="w-full py-2 rounded-xl bg-violet-100 dark:bg-violet-900/30 hover:bg-violet-200 dark:hover:bg-violet-900/50 text-violet-700 dark:text-violet-400 font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  data-testid="download-fec-btn"
                >
                  {generating === 'fec' ? (
                    <SpinnerGap size={16} className="animate-spin" />
                  ) : (
                    <DownloadSimple size={16} />
                  )}
                  Télécharger FEC
                </button>
              </div>
            </div>
          </div>

          {/* CSV Export */}
          <div className="border border-border rounded-2xl p-4 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <FileCsv size={20} className="text-emerald-600" weight="duotone" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">Export CSV</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Tableur pour votre comptable (Excel compatible)
                </p>
                <button 
                  onClick={() => downloadCSV('all')}
                  disabled={generating === 'csv'}
                  className="w-full py-2 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  data-testid="download-csv-btn"
                >
                  {generating === 'csv' ? (
                    <SpinnerGap size={16} className="animate-spin" />
                  ) : (
                    <DownloadSimple size={16} />
                  )}
                  Télécharger CSV
                </button>
              </div>
            </div>
          </div>

          {/* PDF Summary */}
          <div className="border border-border rounded-2xl p-4 hover:border-red-300 dark:hover:border-red-700 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <FilePdf size={20} className="text-red-600" weight="duotone" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">Récapitulatif PDF</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Synthèse comptable de la période
                </p>
                <button 
                  onClick={downloadPDF}
                  disabled={generating === 'pdf'}
                  className="w-full py-2 rounded-xl bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  data-testid="download-pdf-btn"
                >
                  {generating === 'pdf' ? (
                    <SpinnerGap size={16} className="animate-spin" />
                  ) : (
                    <DownloadSimple size={16} />
                  )}
                  Télécharger PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Info Section */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
        <div className="flex gap-3">
          <Calculator size={20} className="text-blue-600 flex-shrink-0 mt-0.5" weight="duotone" />
          <div className="text-sm">
            <p className="font-medium text-blue-800 dark:text-blue-300 mb-1">À propos du FEC</p>
            <p className="text-blue-700 dark:text-blue-400">
              Le Fichier des Écritures Comptables (FEC) est obligatoire en France pour toute entreprise 
              tenant une comptabilité informatisée. Il doit être transmis à l'administration fiscale 
              en cas de contrôle. Le format généré est conforme à la norme DGFiP.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default Compta;
